import { defineWidgetCore } from "../../widgetDefinition";
import { resolveTemplate } from "../../Utils/resolveTemplate";
import type { TableComponentSpec, WidgetResponseSpec } from "../../Processing/types";
import { adaptResponse } from "../../Processing/adapt";
import { formatToDecimals, inferDecimals, roundToDecimals } from "../../Processing/decimals";
import { formatSeriesLabel } from "../../Processing/seriesLabel";
import { categorySortKey } from "../../Utils/tableStyles";
import { renderTable } from "../../PDF/renderers/tablePdfRenderer";
import { formatBuilderDateRange } from "../../PDF/snapshotDateRange";
import { getDebugFlags } from "../../Components/debugFeatures";
import { columnInjuredKey, healthyMean, isInjured } from "../../../../Utils/injury.mjs";

const EMPTY_RESPONSE: WidgetResponseSpec = { widget_id: "", series: [] };

const GROUPBY_LABELS: Record<string, string> = {
  day: "Day", week: "Week", month: "Month", year: "Year",
  player: "Player", position: "Position", drill: "Drill",
};

function groupByLabel(groupBy?: string): string {
  return groupBy ? (GROUPBY_LABELS[groupBy] ?? groupBy) : "";
}

export const tableCore = defineWidgetCore<TableComponentSpec>({
  process(spec, response) {
    const s = spec;
    const adapted = adaptResponse(response ?? EMPTY_RESPONSE, s.table_builder_misc?.groupBy === "drill");

    const hasColors = [...adapted.seriesDataMap.values()].some((sd) =>
      sd.points.some((p) => p != null && typeof p !== "number"),
    );

    const columns = s.series.map((ser) => ({
      key: ser.queryId,
      title: ser.displayNameOverride?.trim()
        ? ser.displayNameOverride.trim()
        : formatSeriesLabel(ser.displayName, ser.metricAgg, ser.metricIdentifierType),
      dataIndex: ser.queryId,
    }));

    // The average rows round to the decimal count the backend used for the series,
    // read from its value labels. A series without labels keeps the historical 1.
    const seriesDecimals = (queryId: string): number =>
      inferDecimals(adapted.seriesDataMap.get(queryId)?.points) ?? 1;

    const rows = adapted.categories.map((cat, i) => {
      const row: Record<string, unknown> = {
        key: String(i),
        category: adapted.labels[i],
        __raw_category: adapted.categories[i],
        __position: adapted.positionMap.get(cat) ?? '',
      };
      for (const ser of s.series) {
        const seriesData = adapted.seriesDataMap.get(ser.queryId);
        const point = seriesData?.points[i];
        row[ser.queryId] =
          point == null ? null : typeof point === "number" ? point : point.y;
        if (hasColors && point != null && typeof point !== "number") {
          row[`__color_${ser.queryId}`] = point.color;
        }
        if (point != null && typeof point !== "number" && point.custom?.value_label != null) {
          row[`__label_${ser.queryId}`] = point.custom.value_label;
        }
        // One flag per column: each column is its own query with its own date range.
        if (point != null && typeof point !== "number" && point.custom?.injured === true) {
          row[columnInjuredKey(ser.queryId)] = true;
        }
      }
      return row;
    });

    // A column average over the healthy rows of that column, rounded to the series decimals.
    const columnAverage = (group: Record<string, unknown>[], queryId: string): number | null =>
      healthyMean(
        group,
        (r: Record<string, unknown>) => (typeof r[queryId] === "number" ? (r[queryId] as number) : null),
        (r: Record<string, unknown>) => isInjured(r, queryId),
      );

    if (s.mutations.showAverageRow && rows.length > 0) {
      const avgRow: Record<string, unknown> = { key: "avg", category: "Average" };
      for (const ser of s.series) {
        const avg = columnAverage(rows, ser.queryId);
        if (avg === null) {
          avgRow[ser.queryId] = null;
          continue;
        }
        const decimals = seriesDecimals(ser.queryId);
        avgRow[ser.queryId] = roundToDecimals(avg, decimals);
        avgRow[`__label_${ser.queryId}`] = formatToDecimals(avg, decimals);
      }
      rows.unshift(avgRow);
    }

    const groupBy = s.table_builder_misc?.groupBy;
    const separateByPosition = s.mutations.separateByPosition && groupBy === "player";

    const sortConfig = s.mutations.sortConfig;
    const validSortKeys = new Set(["category", ...s.series.map((ser) => ser.queryId)]);
    const activeSortConfig = sortConfig && validSortKeys.has(sortConfig.columnKey) ? sortConfig : undefined;

    if (separateByPosition) {
      // Sort by position first; within each group apply sortConfig if present, else player name
      rows.sort((a, b) => {
        if (a.key === "avg") return -1;
        if (b.key === "avg") return 1;
        const posA = String(a.__position ?? "");
        const posB = String(b.__position ?? "");
        const posCmp = posA.localeCompare(posB);
        if (posCmp !== 0) return posCmp;
        if (activeSortConfig) {
          const { columnKey, direction } = activeSortConfig;
          let cmp: number;
          if (columnKey === "category") {
            cmp = categorySortKey(a, groupBy).localeCompare(categorySortKey(b, groupBy));
          } else {
            cmp = (Number(a[columnKey]) || 0) - (Number(b[columnKey]) || 0);
          }
          return direction === "desc" ? -cmp : cmp;
        }
        return categorySortKey(a, groupBy).localeCompare(categorySortKey(b, groupBy));
      });
    } else if (activeSortConfig) {
      const { columnKey, direction } = activeSortConfig;
      rows.sort((a, b) => {
        if (a.key === "avg") return -1;
        if (b.key === "avg") return 1;
        let cmp: number;
        if (columnKey === "category") {
          cmp = categorySortKey(a, groupBy).localeCompare(categorySortKey(b, groupBy));
        } else {
          cmp = (Number(a[columnKey]) || 0) - (Number(b[columnKey]) || 0);
        }
        return direction === "desc" ? -cmp : cmp;
      });
    }

    if (separateByPosition) {
      // Insert a separator row at each position boundary (skipping the avg row)
      const dataRows = rows.filter((r) => r.key !== "avg");
      const avgRows = rows.filter((r) => r.key === "avg");

      // Pre-compute per-position averages for each series column
      const positionGroups = new Map<string, typeof dataRows>();
      for (const row of dataRows) {
        const pos = String(row.__position ?? "");
        if (!positionGroups.has(pos)) positionGroups.set(pos, []);
        positionGroups.get(pos)!.push(row);
      }

      const withSeparators: typeof rows = [];
      let lastPos: string | null = null;
      for (const row of dataRows) {
        const pos = String(row.__position ?? "");
        if (pos !== lastPos) {
          const group = positionGroups.get(pos) ?? [];
          const sepRow: Record<string, unknown> = {
            key: `__pos_sep_${pos || "unassigned"}`,
            category: pos || "Unassigned",
            __is_position_separator: true,
          };
          for (const ser of s.series) {
            const avg = columnAverage(group, ser.queryId);
            if (avg === null) {
              sepRow[ser.queryId] = null;
              continue;
            }
            const decimals = seriesDecimals(ser.queryId);
            sepRow[ser.queryId] = roundToDecimals(avg, decimals);
            sepRow[`__label_${ser.queryId}`] = formatToDecimals(avg, decimals);
          }
          withSeparators.push(sepRow);
          lastPos = pos;
        }
        withSeparators.push(row);
      }
      rows.length = 0;
      rows.push(...avgRows, ...withSeparators);
    }

    // ── DEBUG: pad to 60 rows with deterministic junk data (localhost only) ──
    if (getDebugFlags().padTableRowData) {
      const PAD_TARGET = 60;
      const JUNK_NAMES = [
        "Alpha Bravo", "Charlie Delta", "Echo Foxtrot", "Golf Hotel", "India Juliet",
        "Kilo Lima", "Mike November", "Oscar Papa", "Quebec Romeo", "Sierra Tango",
        "Uniform Victor", "Whiskey Xray", "Yankee Zulu", "Able Baker", "Cast Dog",
        "Easy Fox", "George How", "Item Jig", "King Love", "Mike Nan",
        "Oboe Peter", "Queen Roger", "Sugar Tare", "Uncle Victor", "William Xray",
        "Yoke Zebra", "Red One", "Blue Two", "Gold Three", "Iron Four",
        "Steel Five", "Copper Six", "Silver Seven", "Bronze Eight", "Cobalt Nine",
        "Zinc Ten", "Nickel Ace", "Titanium Bolt", "Carbon Core", "Neon Dash",
        "Argon Edge", "Xenon Flare", "Helium Gust", "Krypton Haze", "Radon Ice",
        "Plasma Jet", "Quartz Knot", "Basalt Loom", "Flint Mesa", "Onyx Pyre",
        "Jade Ridge", "Opal Shard", "Topaz Vale", "Amber Wren", "Coral Yew",
        "Dusk Zenith", "Frost Apex", "Hail Blitz", "Mist Crest", "Storm Delta",
      ];
      while (rows.length < PAD_TARGET) {
        const idx = rows.length;
        const row: Record<string, unknown> = {
          key: `pad-${idx}`,
          category: JUNK_NAMES[idx % JUNK_NAMES.length],
        };
        for (const ser of s.series) {
          row[ser.queryId] = Math.round(((idx * 7 + ser.queryId.length * 13) % 500) * 10) / 10;
        }
        rows.push(row);
      }
    }

    return {
      type: "table",
      spec: s,
      table: {
        columns: [{ key: "category", title: groupByLabel(s.table_builder_misc?.groupBy), dataIndex: "category" }, ...columns],
        rows,
        groupBy: s.table_builder_misc?.groupBy,
      },
    };
  },
  pdf: {
    buildSnapshot({ processed, slicerValues }) {
      if (processed.type !== "table") return null;
      const misc = processed.spec.table_builder_misc;

      const datePart = formatBuilderDateRange(misc);

      const names = misc?.selectedSubjectNames ?? [];
      const MAX = 10;
      const subjectPart = names.length === 0
        ? "Full Team"
        : names.length <= MAX
          ? names.join(", ")
          : `${names.slice(0, MAX).join(", ")} (+${names.length - MAX} more)`;

      const subtitle = datePart ? `${datePart} | ${subjectPart}` : subjectPart;

      // The same fallback as the live widget's header, so the PDF names the table
      // the way the screen does (the series name, not the "[A] ..." column label).
      const title =
        resolveTemplate(processed.spec.display.title ?? "", slicerValues).resolved ||
        processed.spec.series[0]?.displayName ||
        "Table";

      return {
        type: "table",
        title,
        columns: processed.table.columns,
        rows: processed.table.rows,
        groupBy: processed.table.groupBy,
        subtitle,
      };
    },
    render({ snapshot, rect, scale }) {
      if (snapshot.type !== "table") return null;
      return renderTable(snapshot, rect, scale);
    },
  },
});
